import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getDataGapDocs } from './MagicUploadHelper';
import { updateNeedInfoFileNumber } from '../../store/investor/action';
import { useDispatch } from 'react-redux';
import { SOURCE } from '../NeedInfoV2/NeedInfoV2Constants';
import { Mixpanel } from '../../utils/mixpanel';
import { useAppSelector } from '../../app/hooks';
import { getSubdomain } from '../../utils/utils';
import { AICA_DATA_PENDING, AICA_MAGIC_UPLOAD } from '../../utils/constants/mixpanelEvents/investorEvents';
import { DATA_POSITION, MAGIC_UPLOAD_TYPE, USER_TYPES } from '../../store/investor/constants';
import { INVESTOR_ROUTES } from '../../utils/constants/routesConst';
import { BOOLEAN_CHOICE } from '../../utils/constants/commonConstants';

const useMagicUploadLogic = () => {
    const [params, setParams] = useSearchParams();
    const location = useLocation();
    const dispatch = useDispatch();
    const param = useParams();
    const [isVisible, setIsVisible] = useState(false);
    const [drawerContent, setDrawerContent] = useState<'needInfo' | 'magicUpload'>('magicUpload');
    const userData = useAppSelector((state) => state.user.userData);
    const localUserData = JSON.parse(localStorage.getItem('user_data') || '{}');
    const isPartner = userData?.accountType === 'PARTNER';

    useEffect(() => {
        const magicUpload = params.get('magicUpload') === 'true';
        const needInfo = params.get('needInfo') === 'true';

        // Only set default content when drawer is first opening (not when switching tabs)
        // If drawer is already visible, don't override user's tab selection
        if (!isVisible) {
            // Prioritize needInfo over magicUpload to ensure data pending opens correctly
            if (needInfo) {
                setDrawerContent('needInfo');
                setIsVisible(true);
            } else if (magicUpload || (isPartner && magicUpload)) {
                setDrawerContent('magicUpload');
                setIsVisible(true);
            }
        }
    }, [params, isPartner]);

    const changeDrawerContent = (content: 'needInfo' | 'magicUpload') => {
        setDrawerContent(content);
        // change the url
        if (content === 'needInfo') {
            params.set('needInfo', 'true');
            if (params.get('isCompany') === 'true' || param.id !== '') params.set(SOURCE.BORROWER_REQ, 'true');
            else params.set(SOURCE.LENDER_REQ, 'true');
            params.delete('magicUpload');

            const isZeusCompany =
                userData?.accountType === USER_TYPES.COMPANY_TYPE && localUserData?.accountType === USER_TYPES.INVESTOR_TYPE;
            const zeusCompany = isZeusCompany ? BOOLEAN_CHOICE.YES : BOOLEAN_CHOICE.NO;

            Mixpanel.track(AICA_DATA_PENDING, {
                userRole: userData?.accountType,
                pnoPlatform: getSubdomain(),
                pnoId: userData?.investorOrgId,
                userId: userData?.userId,
                currentUrl: window.location.href,
                zeusCompany: zeusCompany,
                position: params.get('type') === INVESTOR_ROUTES.SCAN ? DATA_POSITION.REPORT : params.get('type') ?? DATA_POSITION.COMPANY,
                organizationId: param.id ?? userData?.assesseeOrgId ?? params.get('assesseeId'),
            });
        } else {
            params.set('magicUpload', 'true');
            if (params.get(SOURCE.BORROWER_REQ) === 'true' || userData?.accountType === 'PARTNER') {
                params.set('isCompany', 'true');
                // For Partners, also show the Pending Data tab option
                if (userData?.accountType === 'PARTNER') {
                    params.set('showNeedInfo', 'true');
                }
            }
            params.delete('needInfo');
            params.delete(SOURCE.BORROWER_REQ);
            params.delete(SOURCE.LENDER_REQ);

            Mixpanel.track(AICA_MAGIC_UPLOAD, {
                userRole: userData?.accountType,
                pnoPlatform: getSubdomain(),
                pnoId: userData?.investorOrgId,
                userId: userData?.userId,
                currentUrl: window.location.href,
                accessedPlatform: userData?.accountType,
                individualGlobal: MAGIC_UPLOAD_TYPE.INDIVIDUAL,
                organizationId: param.id ?? userData?.assesseeOrgId,
            });
        }
        setParams(params, { state: location.state });
    };

    // const getPendingDocumentsCount = () => {
    //     getDataGapDocs(
    //         {
    //             assesseeOrgId: param.id ?? '',
    //         },
    //         {
    //             onSuccess: (res) => {
    //                 dispatch(updateNeedInfoFileNumber(res.pendingCount));
    //             },
    //         },
    //     );
    // };

    const toggleDrawer = () => {
        if (window.location.pathname.includes(INVESTOR_ROUTES.APP)) {
            params.set('refetchOrgInfo', 'true');
        }
        if ([INVESTOR_ROUTES.APP_WATCH, INVESTOR_ROUTES.APP_SCAN, INVESTOR_ROUTES.APP_TRACK].includes(window.location.pathname)) {
            params.delete('partnerOrgId');
        }
        setIsVisible(false);
        // if (params.get('needInfo') === 'true') {
        //     getPendingDocumentsCount();
        // }
        params.delete('magicUpload');
        params.delete('filters');
        params.delete('isCompany');
        params.delete('needInfo');
        params.delete('showNeedInfo');
        params.delete('showUntaggedOrg');
        params.delete('hideMu');
        params.delete('tabState');
        params.delete('disableSendReminder');
        params.delete('dealId');
        params.delete('readOnly');
        params.delete(SOURCE.BORROWER_REQ);
        params.delete(SOURCE.LENDER_REQ);
        params.delete('disableAnimation');
        params.delete('expandedPill');
        params.delete('assesseeId');
        params.delete('lenderId');
        params.delete('companyName');
        params.delete('lenderName');
        params.delete('companyNameInfo');
        params.delete('orgName');
        params.delete('orgId');
        setParams(params, { state: location.state });
    };

    return {
        isVisible,
        toggleDrawer,
        setIsVisible,
        drawerContent,
        changeDrawerContent,
    };
};

export default useMagicUploadLogic;
